/**
 * LaunchReadinessPage — J2-I0 "what is left to launch" surface.
 *
 * Route: /project/:projectId/launch
 * Data:  GET /api/projects/:projectId/launch/v1/readiness (no-store)
 *
 * Composition per .design/journey-program-wave1/mockups/launch-readiness-page.html:
 * title + subtitle + refresh, goal tabs with done/total chips, milestone rows
 * (one outlined action per missing row, RETRY per unavailable row — the domain
 * milestone is a plain row too, DC-19: the live section is hosted by
 * launch-activation's DomainConnectDrawer), the emphasized first-party opt-in
 * trap, and the safe-rollout panel (J2-I2) at the bottom anchor.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LAUNCH_READINESS_TEST_IDS } from '../testIds';
import type { LaunchGoalSet } from '../types/launchReadiness.types';
import { defaultGoalTab, filterMilestonesByGoal } from '../utils/goalMilestones';
import { useLaunchReadiness } from '../hooks/useLaunchReadiness';
import { GoalTabs } from '../components/GoalTabs';
import { MilestoneRow } from '../components/MilestoneRow';
import { SafeRolloutPanel } from '../components/SafeRolloutPanel';

const CONTENT_MAX_WIDTH = 980;

function LoadingSkeleton() {
  return (
    <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
      <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
      <Paper>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5 }}>
            <Skeleton variant="circular" width={22} height={22} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="70%" />
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

export const LaunchReadinessPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data, loading, refreshing, error, refetch } = useLaunchReadiness(projectId);

  // Tab selection: default = first goal with missing items, chosen once per
  // loaded snapshot generation; the user's manual choice is never overridden
  // by a refetch.
  const [selectedGoal, setSelectedGoal] = useState<LaunchGoalSet | null>(null);
  useEffect(() => {
    if (data && selectedGoal === null) {
      setSelectedGoal(defaultGoalTab(data.milestones));
    }
  }, [data, selectedGoal]);

  const activeGoal: LaunchGoalSet = selectedGoal ?? 'first_value';

  const visibleMilestones = useMemo(
    () => (data ? filterMilestonesByGoal(data.milestones, activeGoal) : []),
    [data, activeGoal],
  );

  // DC-19: the domain requirement is a plain row here; the live section is hosted by
  // launch-activation's DomainConnectDrawer, never a prose reproduction of the DNS table.
  const rowMilestones = visibleMilestones;

  const handleAction = (deepLink: string) => navigate(deepLink);
  const handleRetry = () => { void refetch(); };

  return (
    <Box data-testid={LAUNCH_READINESS_TEST_IDS.page} sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: CONTENT_MAX_WIDTH }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Launch readiness
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Everything left between this funnel and live traffic — the same checks the CLI preflight runs.
          </Typography>
        </Box>
        <Tooltip title="Refresh readiness">
          <span>
            <IconButton onClick={handleRetry} disabled={loading || refreshing} aria-label="Refresh readiness">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Full-page error state (no white page on 5xx) */}
      {error && !data && !loading && (
        <Alert
          severity="error"
          sx={{ maxWidth: CONTENT_MAX_WIDTH }}
          action={(
            <Button
              data-testid={LAUNCH_READINESS_TEST_IDS.retry}
              color="inherit"
              size="small"
              onClick={handleRetry}
            >
              Retry
            </Button>
          )}
        >
          <AlertTitle>Could not load launch readiness</AlertTitle>
          {error.message}
        </Alert>
      )}

      {loading && !error && <LoadingSkeleton />}

      {data && !loading && (
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
          {/* A refetch that failed while stale data is on screen */}
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Refresh failed: {error.message}
            </Alert>
          )}

          <GoalTabs
            milestones={data.milestones}
            value={activeGoal}
            onChange={setSelectedGoal}
          />

          <Paper>
            {rowMilestones.map((milestone) => (
              <MilestoneRow
                key={milestone.id}
                milestone={milestone}
                onAction={handleAction}
                onRetry={handleRetry}
              />
            ))}
            {rowMilestones.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
                No milestones apply to this goal.
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* Safe-rollout panel (J2-I2), mounted at the reserved anchor position */}
      <Box
        data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutAnchor}
        sx={{ maxWidth: CONTENT_MAX_WIDTH, mt: 4 }}
      >
        <SafeRolloutPanel projectId={projectId} />
      </Box>
    </Box>
  );
};

export default LaunchReadinessPage;
